import {
  APPOINTMENT_BOOKED_BY,
  APPOINTMENT_CRM_STATUS,
  APPOINTMENT_STATUSES,
  RESOLVE_BLOCKED_REASON,
  type AppointmentBlockedCounts,
  type AppointmentBlockedCountsByCreator,
  type AppointmentBookedBy,
  type AppointmentFilter,
  type AppointmentStatus,
  type AppointmentViewMode,
  type CalendarView,
} from "@/features/clinic/types/appointment";

/** `?queue=to-collect` - the named worklist the payment nudge emails link to. */
export const TO_COLLECT_QUEUE = "to-collect";

/**
 * The payment queue's original name. Nudge emails sent before the rename link
 * to it, and an email link that opens an unfiltered board reads as broken -
 * so the old name keeps parsing, it just never gets written again.
 */
const LEGACY_TO_INVOICE_QUEUE = "to-invoice";

/** `?queue=unresolved` - the named worklist the stale-status nudge links to. */
export const UNRESOLVED_QUEUE = "unresolved";

/**
 * `?queue=outcome` - consultations whose patient asked for time to decide.
 * The second half of the same question the unresolved queue asks, and where the
 * follow-up nudge's email lands.
 */
export const OUTCOME_QUEUE = "outcome";

/**
 * `?queue=attention` - every open question at once, whatever its reason. The
 * landing point for "N need attention", and the tab the narrower three hang off.
 */
export const ATTENTION_QUEUE = "attention";

/**
 * `?queue=missing-details` - upcoming bookings that never said what they are
 * for, or where. Not a resolve block (nothing is overdue), but the same kind
 * of pile: rows a person has to go and finish.
 */
export const MISSING_DETAILS_QUEUE = "missing-details";

/**
 * `?queue=crm-failed` - booked here, refused by the CRM, so its schedule does
 * not show the patient. Nothing is overdue and nobody is waiting on an answer:
 * it is broken rather than owed, and the fix is a retry, not a decision. Where
 * the dashboard's "N could not be created" alert lands.
 */
export const CRM_FAILED_QUEUE = "crm-failed";

/** One param holds one value, so two worklists cannot both be on. */
export type AppointmentQueue =
  | typeof ATTENTION_QUEUE
  | typeof TO_COLLECT_QUEUE
  | typeof UNRESOLVED_QUEUE
  | typeof OUTCOME_QUEUE
  | typeof MISSING_DETAILS_QUEUE
  | typeof CRM_FAILED_QUEUE;

/**
 * What one worklist asks the API for. The queue names predate the stored-block
 * model and live in nudge emails already sent, so they map onto reasons here
 * rather than being renamed.
 */
const QUEUE_FILTERS: Record<AppointmentQueue, AppointmentFilter> = {
  [ATTENTION_QUEUE]: {
    blockedReasons: [
      RESOLVE_BLOCKED_REASON.PAYMENT_MISSING,
      RESOLVE_BLOCKED_REASON.STALE_STATUS,
      RESOLVE_BLOCKED_REASON.WAITING_ON_USER,
    ],
  },
  [TO_COLLECT_QUEUE]: {
    blockedReasons: [RESOLVE_BLOCKED_REASON.PAYMENT_MISSING],
  },
  [UNRESOLVED_QUEUE]: { blockedReasons: [RESOLVE_BLOCKED_REASON.STALE_STATUS] },
  [OUTCOME_QUEUE]: { blockedReasons: [RESOLVE_BLOCKED_REASON.WAITING_ON_USER] },
  [MISSING_DETAILS_QUEUE]: { missingDetails: true },
  [CRM_FAILED_QUEUE]: { crmStatus: APPOINTMENT_CRM_STATUS.NOT_SYNCED },
};

const QUEUES = Object.keys(QUEUE_FILTERS) as AppointmentQueue[];

export const queueFilter = (kind: AppointmentQueue): AppointmentFilter =>
  QUEUE_FILTERS[kind];

/** Which counters on the blocked-counts response each queue sums. */
const QUEUE_COUNT_KEYS: Record<
  AppointmentQueue,
  (keyof AppointmentBlockedCounts)[]
> = {
  [ATTENTION_QUEUE]: ["paymentMissing", "staleStatus", "waitingOnUser"],
  [TO_COLLECT_QUEUE]: ["paymentMissing"],
  [UNRESOLVED_QUEUE]: ["staleStatus"],
  [OUTCOME_QUEUE]: ["waitingOnUser"],
  [MISSING_DETAILS_QUEUE]: ["missingDetails"],
  [CRM_FAILED_QUEUE]: ["crmPushFailed"],
};

export const queueCount = (
  counts: AppointmentBlockedCounts,
  kind: AppointmentQueue,
): number =>
  QUEUE_COUNT_KEYS[kind].reduce((total, key) => total + counts[key], 0);

export const APPOINTMENT_BOOKED_BY_SOURCES: AppointmentBookedBy[] = [
  APPOINTMENT_BOOKED_BY.USER,
  APPOINTMENT_BOOKED_BY.AI,
  APPOINTMENT_BOOKED_BY.CRM,
];

/**
 * Who booked the appointments a worklist shows. Narrower than the board's
 * "Booked by" facet on purpose: this answers "is this mine to chase, or the
 * agent's mess?", so a teammate's and the CRM's bookings live under `all`
 * rather than earning tabs nobody stands in.
 */
export const APPOINTMENT_CREATOR = {
  ALL: "all",
  ME: "me",
  AI: "ai",
} as const;

export type AppointmentCreator =
  (typeof APPOINTMENT_CREATOR)[keyof typeof APPOINTMENT_CREATOR];

const CREATORS: AppointmentCreator[] = [
  APPOINTMENT_CREATOR.ALL,
  APPOINTMENT_CREATOR.ME,
  APPOINTMENT_CREATOR.AI,
];

/** Which bucket of the counts response a creator tab reads. */
export const creatorCounts = (
  counts: AppointmentBlockedCountsByCreator,
  creator: AppointmentCreator,
): AppointmentBlockedCounts => {
  if (creator === APPOINTMENT_CREATOR.ME) return counts.mine;
  if (creator === APPOINTMENT_CREATOR.AI) return counts.ai;

  return counts;
};

/**
 * `me` keys on the user id rather than `bookedBy: ["user"]` so it means the
 * signed-in agent and not the whole team; `ai` is autopilot only, leaving an
 * AI-drafted booking a human approved under `me`. Falls back to unnarrowed
 * while the current user id is still resolving, so the list never renders as
 * empty when it merely does not know who is asking yet.
 */
export const creatorFilter = (
  creator: AppointmentCreator,
  currentUserId: string | null,
): Pick<AppointmentFilter, "bookedBy" | "bookedByUserIds"> => {
  if (creator === APPOINTMENT_CREATOR.AI) {
    return { bookedBy: [APPOINTMENT_BOOKED_BY.AI] };
  }

  if (creator === APPOINTMENT_CREATOR.ME && currentUserId) {
    return { bookedByUserIds: [currentUserId] };
  }

  return {};
};

export type AppointmentUrlFilters = {
  viewMode: AppointmentViewMode;
  calendarView: CalendarView;
  date: Date;
  statuses: AppointmentStatus[];
  bookedBy: AppointmentBookedBy[];
  /** `null` means "not narrowed" - every doctor, including ones not loaded yet. */
  doctorIds: string[] | null;
  queue: AppointmentQueue | null;
  /** Who booked the rows a worklist shows. Only ever read in queue mode. */
  createdBy: AppointmentCreator;
  /**
   * `?resolve=<id>` - open the answer flow on this appointment. The nudge and
   * escalation emails carry it alongside `queue`, so the link lands on the
   * question itself and falls back to the right worklist when dismissed.
   */
  resolveAppointmentId: string | null;
};

const CALENDAR_VIEWS: CalendarView[] = ["day", "week", "month"];

/**
 * An absent param means "the default"; a present-but-empty one means "nothing
 * selected". Without that distinction `?status=` would read as unfiltered and
 * silently widen the list back out on reload.
 */
const parseList = <T extends string>(
  raw: string | null,
  allowed: T[],
  fallback: T[],
): T[] => {
  if (raw === null) return fallback;
  if (raw === "") return [];

  return raw
    .split(",")
    .filter((value): value is T => allowed.includes(value as T));
};

export const parseAppointmentFilters = (
  params: URLSearchParams,
  defaultDate?: Date,
): AppointmentUrlFilters => {
  const rawDate = params.get("date");
  const parsedDate = rawDate ? new Date(`${rawDate}T00:00:00`) : null;
  const rawCalendarView = params.get("calendar") as CalendarView | null;
  const rawDoctors = params.get("doctor");
  // A worklist only exists as a list, so a link straight to one doesn't need to
  // spell out `view=list` too.
  const rawQueue =
    params.get("queue") === LEGACY_TO_INVOICE_QUEUE
      ? TO_COLLECT_QUEUE
      : (params.get("queue") as AppointmentQueue | null);
  const queue = rawQueue && QUEUES.includes(rawQueue) ? rawQueue : null;
  const rawCreatedBy = params.get("createdBy") as AppointmentCreator | null;

  return {
    viewMode: params.get("view") === "list" || queue ? "list" : "calendar",
    calendarView:
      rawCalendarView && CALENDAR_VIEWS.includes(rawCalendarView)
        ? rawCalendarView
        : "day",
    date:
      parsedDate && !Number.isNaN(parsedDate.getTime())
        ? parsedDate
        : (defaultDate ?? new Date()),
    statuses: parseList(
      params.get("status"),
      [...APPOINTMENT_STATUSES],
      [...APPOINTMENT_STATUSES],
    ),
    bookedBy: parseList(
      params.get("bookedBy"),
      APPOINTMENT_BOOKED_BY_SOURCES,
      APPOINTMENT_BOOKED_BY_SOURCES,
    ),
    doctorIds:
      rawDoctors === null
        ? null
        : rawDoctors === ""
          ? []
          : rawDoctors.split(","),
    queue,
    createdBy:
      rawCreatedBy && CREATORS.includes(rawCreatedBy)
        ? rawCreatedBy
        : APPOINTMENT_CREATOR.ALL,
    resolveAppointmentId: params.get("resolve"),
  };
};

const setOrDelete = (
  params: URLSearchParams,
  key: string,
  value: string | null,
) => {
  if (value === null) params.delete(key);
  else params.set(key, value);
};

/** Defaults are never written, so an untouched board keeps a clean URL. */
export const writeAppointmentFilters = (
  params: URLSearchParams,
  next: Partial<AppointmentUrlFilters>,
): URLSearchParams => {
  if (next.viewMode !== undefined) {
    setOrDelete(params, "view", next.viewMode === "list" ? "list" : null);
  }

  if (next.calendarView !== undefined) {
    setOrDelete(
      params,
      "calendar",
      next.calendarView === "day" ? null : next.calendarView,
    );
  }

  if (next.date !== undefined) {
    const year = next.date.getFullYear();
    const month = String(next.date.getMonth() + 1).padStart(2, "0");
    const day = String(next.date.getDate()).padStart(2, "0");

    setOrDelete(params, "date", `${year}-${month}-${day}`);
  }

  if (next.statuses !== undefined) {
    setOrDelete(
      params,
      "status",
      next.statuses.length === APPOINTMENT_STATUSES.length
        ? null
        : next.statuses.join(","),
    );
  }

  if (next.bookedBy !== undefined) {
    setOrDelete(
      params,
      "bookedBy",
      next.bookedBy.length === APPOINTMENT_BOOKED_BY_SOURCES.length
        ? null
        : next.bookedBy.join(","),
    );
  }

  if (next.doctorIds !== undefined) {
    setOrDelete(
      params,
      "doctor",
      next.doctorIds === null ? null : next.doctorIds.join(","),
    );
  }

  if (next.queue !== undefined) {
    setOrDelete(params, "queue", next.queue);
  }

  if (next.createdBy !== undefined) {
    setOrDelete(
      params,
      "createdBy",
      next.createdBy === APPOINTMENT_CREATOR.ALL ? null : next.createdBy,
    );
  }

  if (next.resolveAppointmentId !== undefined) {
    setOrDelete(params, "resolve", next.resolveAppointmentId);
  }

  return params;
};
